import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, RotateCcw, Eye, Edit3, Info } from "lucide-react";

const VARIABLES = [
  ["{{candidate_name}}", "Candidate full name"],
  ["{{first_name}}", "Candidate first name"],
  ["{{candidate_phone}}", "Candidate phone"],
  ["{{today_long}}", "Today, e.g. 'April 24th, 2026'"],
  ["{{signing_deadline_long}}", "Signing deadline (today + 3 days)"],
  ["{{boutique_name}}", "Boutique display name"],
  ["{{boutique_legal_name}}", "Legal entity, e.g. 'Green Massage of Plantation DBA The NOW Massage'"],
  ["{{boutique_address}}", "Boutique street address"],
  ["{{position}}", "Massage Therapist / Experience Guide"],
  ["{{employment_type}}", "Part-Time / Full-Time"],
  ["{{start_date}}", "Target start date"],
  ["{{pay_rate}}", "Hourly rate"],
  ["{{sender_name}}", "Sender's name (defaults to signed-in user)"],
  ["{{sender_title}}", "Sender's title"],
  ["{{director_name}}", "Director of Operations name"],
];

export default function OfferTemplatesDialog({ onClose }) {
  const [templates, setTemplates] = useState(null);
  const [role, setRole] = useState("MT");
  const [mode, setMode] = useState("edit"); // "edit" | "preview"
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/hiring/offer-templates").then((r) => { setTemplates(r.data); setDirty(false); }).catch((e) => toast.error(e?.response?.data?.detail || "Load failed"));

  useEffect(() => { load(); }, []);

  const current = templates?.[role];
  const setBody = (newBody) => {
    setTemplates((t) => ({ ...t, [role]: { ...t[role], body: newBody, is_default: false } }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/hiring/offer-templates/${role}`, { role, body: current.body });
      toast.success("Template saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${role} template to the default? Any custom edits will be lost.`)) return;
    setBusy(true);
    try {
      await api.post(`/hiring/offer-templates/${role}/reset`);
      toast.success("Reset to default");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap" onClick={onClose} data-testid="offer-templates-dialog">
      <div className="bg-white rounded-2xl w-full max-w-5xl h-[90vh] shadow-xl flex flex-col mobile-modal-card" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center px-5 py-3 border-b border-stone-100">
          <div>
            <div className="text-[10px] label-eyebrow text-stone-400">Sheila's screen</div>
            <div className="font-serif-display text-lg text-stone-900">Offer letter templates</div>
          </div>
          <div className="ml-6 flex items-center gap-1 bg-stone-100 rounded-full p-1">
            <button onClick={() => setRole("MT")} data-testid="tpl-role-MT" className={`px-3 py-1 rounded-full text-xs transition-colors ${role === "MT" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-800"}`}>Massage Therapist</button>
            <button onClick={() => setRole("EG")} data-testid="tpl-role-EG" className={`px-3 py-1 rounded-full text-xs transition-colors ${role === "EG" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-800"}`}>Experience Guide</button>
          </div>
          <div className="ml-auto flex items-center gap-1 bg-stone-100 rounded-full p-1">
            <button onClick={() => setMode("edit")} data-testid="tpl-mode-edit" className={`px-3 py-1 rounded-full text-xs flex items-center gap-1 ${mode === "edit" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-800"}`}><Edit3 size={11} strokeWidth={1.5} /> Edit</button>
            <button onClick={() => setMode("preview")} data-testid="tpl-mode-preview" className={`px-3 py-1 rounded-full text-xs flex items-center gap-1 ${mode === "preview" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-800"}`}><Eye size={11} strokeWidth={1.5} /> Preview</button>
          </div>
          <button onClick={onClose} data-testid="tpl-close" className="ml-3 p-1 text-stone-400 hover:text-stone-800"><X size={16} strokeWidth={1.5} /></button>
        </header>

        {!current ? (
          <div className="flex-1 flex items-center justify-center text-sm text-stone-400">Loading…</div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {mode === "edit" ? (
              <>
                <textarea
                  value={current.body}
                  onChange={(e) => setBody(e.target.value)}
                  data-testid="tpl-body"
                  className="flex-1 p-5 text-sm font-mono outline-none resize-none border-r border-stone-100"
                  spellCheck={false}
                />
                <aside className="w-72 shrink-0 overflow-y-auto p-4 bg-stone-50">
                  <div className="flex items-center gap-1.5 text-[10px] label-eyebrow text-stone-500 mb-2">
                    <Info size={11} strokeWidth={1.5} /> Variables
                  </div>
                  <ul className="space-y-2 text-xs">
                    {VARIABLES.map(([v, desc]) => (
                      <li key={v}>
                        <button
                          onClick={() => navigator.clipboard.writeText(v).then(() => toast.success(`Copied ${v}`))}
                          className="w-full text-left px-2 py-1 rounded bg-white hover:bg-stone-100 border border-stone-200 text-stone-700 font-mono text-[11px]"
                          data-testid={`tpl-var-${v.replace(/\{|\}/g, "")}`}
                        >
                          {v}
                        </button>
                        <div className="text-[10px] text-stone-500 mt-0.5 px-2">{desc}</div>
                      </li>
                    ))}
                  </ul>
                </aside>
              </>
            ) : (
              <div className="flex-1 overflow-y-auto p-6 offer-preview">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{current.body}</ReactMarkdown>
              </div>
            )}
          </div>
        )}

        <footer className="flex items-center px-5 py-3 border-t border-stone-100 bg-stone-50 rounded-b-2xl gap-3">
          {current && (
            <>
              <button
                onClick={reset}
                disabled={busy || current.is_default}
                data-testid="tpl-reset"
                className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 disabled:opacity-40"
              >
                <RotateCcw size={11} strokeWidth={1.5} /> Reset to default
              </button>
              <div className="text-[10px] text-stone-400">
                {current.is_default ? "Showing default template" : `Last edited by ${current.updated_by_name || "?"} · ${(current.updated_at || "").slice(0, 10)}`}
              </div>
            </>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} className="text-xs text-stone-500 px-3 py-1.5 rounded-full hover:bg-stone-100">Close</button>
            <button
              onClick={save}
              disabled={busy || !dirty || !current}
              data-testid="tpl-save"
              className="text-xs px-4 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50"
            >
              {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </button>
          </div>
        </footer>
      </div>
      <style>{`
        .offer-preview h3 { font-family: var(--font-serif, Georgia, serif); font-size: 1.05rem; color: #1c1917; margin-top: 1.25rem; margin-bottom: 0.4rem; }
        .offer-preview p { color: #44403c; font-size: 0.85rem; line-height: 1.55; margin: 0.5rem 0; }
        .offer-preview strong { color: #1c1917; }
        .offer-preview em { color: #57534e; }
        .offer-preview ul, .offer-preview ol { padding-left: 1.25rem; font-size: 0.85rem; color: #44403c; margin: 0.4rem 0; }
        .offer-preview li { margin: 0.15rem 0; }
        .offer-preview table { border-collapse: collapse; margin: 0.75rem 0; font-size: 0.75rem; }
        .offer-preview th, .offer-preview td { border: 1px solid #e7e5e4; padding: 0.3rem 0.55rem; text-align: left; }
        .offer-preview th { background: #fafaf9; font-weight: 500; color: #292524; }
      `}</style>
    </div>
  );
}
